import { ArrowDown, ArrowUp } from 'lucide-react';
import type { ScreenRecord } from '../extension-types.js';
import { recordTitle } from '../lib/format.js';
import type { AppEntity, AppField } from '../lib/meta.js';
import { FieldValue, RecordLink } from './fields.js';
import { cn, EmptyState, Table, TD, TH } from './ui.js';

const NUMERIC = new Set(['money', 'integer', 'decimal', 'percent']);

/** A table of records with the title column linking to each record. Used by lists and related lists. */
export function RecordTable({
  entity,
  records,
  columns,
  titles,
  empty = `No ${entity.plural.toLowerCase()} yet`,
  sort,
  onSort,
  onStackedChange,
}: {
  entity: AppEntity;
  records: ScreenRecord[];
  columns: AppField[];
  titles?: Record<string, string>;
  empty?: string;
  sort?: { field: string; dir: 'asc' | 'desc' } | null;
  onSort?: (field: string) => void;
  /** Hears when the rows turn into cards (no column headings to sort by) and back. */
  onStackedChange?: (stacked: boolean) => void;
}) {
  if (records.length === 0) return <EmptyState title={empty} />;
  return (
    <Table stack {...(onStackedChange && { onStackedChange })}>
      <thead>
        <tr>
          {columns.map((c) => {
            const active = sort?.field === c.key;
            const align = NUMERIC.has(c.type) ? 'right' : 'left';
            return (
              <TH key={c.key} align={align}>
                {onSort ? (
                  <button
                    type="button"
                    onClick={() => onSort(c.key)}
                    className={cn(
                      'inline-flex items-center gap-1 uppercase hover:text-slate-800',
                      active && 'text-slate-800',
                    )}
                  >
                    {c.label}
                    {active &&
                      (sort?.dir === 'asc' ? (
                        <ArrowUp className="size-3" aria-label="ascending" />
                      ) : (
                        <ArrowDown className="size-3" aria-label="descending" />
                      ))}
                  </button>
                ) : (
                  c.label
                )}
              </TH>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {records.map((record) => (
          <tr key={record.id} className="hover:bg-slate-50">
            {columns.map((c) => (
              <TD
                key={c.key}
                align={NUMERIC.has(c.type) ? 'right' : 'left'}
                label={c.label}
                primary={c.key === entity.title_field}
              >
                {c.key === entity.title_field ? (
                  <RecordLink entity={entity.id} id={record.id}>
                    {recordTitle(entity, record)}
                  </RecordLink>
                ) : (
                  <FieldValue
                    entity={entity}
                    field={c}
                    record={record}
                    {...(titles !== undefined && { titles })}
                  />
                )}
              </TD>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
